import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Download, FileText, FileSpreadsheet, FileType2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Multi-survey report builder.
 *
 * - Lists every survey the current user can access (the backend already
 *   filters this for creators vs admins, so we just iterate accessible ones).
 * - User picks one or more surveys, an optional date range, and a format.
 * - We POST to /api/analytics/report and download the response as a file.
 */
const ReportsTab = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState('pdf');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [includeAi, setIncludeAi] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null); // {summary, model, surveys_count, responses_count, generated_at}
  const [aiError, setAiError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/surveys`, { withCredentials: true });
        if (!cancelled) setSurveys(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError('Could not load your surveys.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(surveys.map((s) => s.id)));
  const clearAll = () => setSelected(new Set());

  const totalSelectedResponses = surveys
    .filter((s) => selected.has(s.id))
    .reduce((sum, s) => sum + (s.response_count || 0), 0);

  const handleGenerate = async () => {
    setError('');
    if (selected.size === 0) {
      setError('Pick at least one survey.');
      return;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setError('“From” date must be before “To” date.');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        survey_ids: Array.from(selected),
        format,
        include_ai_summary: format === 'pdf' && includeAi,
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      };
      const res = await axios.post(`${API_URL}/api/analytics/report`, payload, {
        withCredentials: true,
        responseType: 'blob',
      });

      // Pull filename from Content-Disposition if present, otherwise fall back
      let filename = `TMC_Report.${format === 'excel' ? 'xlsx' : format}`;
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      if (match) filename = match[1];

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // axios returns the error body as a Blob because we set responseType
      let msg = 'Could not generate report.';
      try {
        const blob = e.response?.data;
        if (blob && blob.text) {
          const txt = await blob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        }
      } catch (_) { /* ignore */ }
      setError(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleAiSummary = async () => {
    setAiError('');
    setAiSummary(null);
    if (selected.size === 0) {
      setAiError('Pick at least one survey first.');
      return;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setAiError('“From” date must be before “To” date.');
      return;
    }
    setAiLoading(true);
    try {
      const payload = {
        survey_ids: Array.from(selected),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      };
      const { data } = await axios.post(`${API_URL}/api/analytics/ai-summary`, payload, {
        withCredentials: true,
      });
      setAiSummary(data);
    } catch (e) {
      setAiError(e?.response?.data?.detail || 'Could not generate AI insights.');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <div>
        <h2 className="text-2xl font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Reports
        </h2>
        <p className="text-sm text-[#5C635A] mt-1">
          Pick one or more surveys, an optional date range, and a format. Creators see only their own surveys; admins can pick any.
        </p>
      </div>

      {error && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          data-testid="reports-error"
        >
          {error}
        </div>
      )}

      <div className="bg-white border border-[#E5E0D8] rounded-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E0D8]">
          <div className="flex items-center gap-3">
            <FileText size={18} className="text-[#7A8B7A]" />
            <span className="text-sm font-medium text-[#2C302E]">
              {selected.size} of {surveys.length} surveys selected
            </span>
            <span className="text-xs text-[#8E958C]">
              ({totalSelectedResponses} total responses)
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-sm px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#5C635A] hover:bg-[#F4F1EA]"
              data-testid="reports-select-all"
              disabled={surveys.length === 0}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-sm px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#5C635A] hover:bg-[#F4F1EA]"
              data-testid="reports-clear-all"
              disabled={selected.size === 0}
            >
              Clear
            </button>
          </div>
        </div>

        {surveys.length === 0 ? (
          <p className="px-6 py-10 text-sm text-[#8E958C] text-center">
            You don’t have any surveys yet. Create one first, then come back here to run a report.
          </p>
        ) : (
          <ul className="divide-y divide-[#E5E0D8]">
            {surveys.map((s) => {
              const checked = selected.has(s.id);
              return (
                <li key={s.id}>
                  <label
                    className="flex items-center gap-3 px-6 py-3 cursor-pointer hover:bg-[#FDFBF7]"
                    data-testid={`reports-survey-${s.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(s.id)}
                      className="h-4 w-4 accent-[#7A8B7A]"
                      data-testid={`reports-check-${s.id}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#2C302E]">{s.name}</div>
                      <div className="text-xs text-[#8E958C]">
                        {s.response_count || 0} responses · {(s.questions || []).length} questions
                        {s.active ? '' : ' · inactive'}
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="bg-white border border-[#E5E0D8] rounded-xl p-6 grid gap-6 sm:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-[#2C302E] mb-3">Date range (optional)</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8E958C] mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                data-testid="reports-date-from"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8E958C] mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                data-testid="reports-date-to"
              />
            </div>
          </div>
          <p className="text-xs text-[#8E958C] mt-2">
            Filters responses by their submission date. Leave blank to include everything.
          </p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-[#2C302E] mb-3">Format</h3>
          <div className="grid grid-cols-3 gap-2">
            <FormatOption
              icon={<FileType2 size={18} />}
              label="PDF"
              hint="Printable summary"
              selected={format === 'pdf'}
              onClick={() => setFormat('pdf')}
              testId="reports-fmt-pdf"
            />
            <FormatOption
              icon={<FileSpreadsheet size={18} />}
              label="Excel"
              hint="One sheet per survey"
              selected={format === 'excel'}
              onClick={() => setFormat('excel')}
              testId="reports-fmt-excel"
            />
            <FormatOption
              icon={<FileText size={18} />}
              label="CSV"
              hint="Raw long-format data"
              selected={format === 'csv'}
              onClick={() => setFormat('csv')}
              testId="reports-fmt-csv"
            />
          </div>
          {format === 'pdf' && (
            <label className="mt-3 flex items-center gap-2 text-sm text-[#2C302E] cursor-pointer">
              <input
                type="checkbox"
                checked={includeAi}
                onChange={(e) => setIncludeAi(e.target.checked)}
                className="h-4 w-4 accent-[#7A8B7A]"
                data-testid="reports-include-ai"
              />
              <Sparkles size={14} className="text-[#C67D63]" />
              <span>Include AI-written executive summary (Claude)</span>
            </label>
          )}
        </div>
      </div>

      {/* AI Insights panel */}
      <div className="bg-gradient-to-br from-[#FDF8F4] to-[#F5F0EA] border border-[#E5D9C8] rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-full shadow-sm border border-[#E5D9C8]">
              <Sparkles size={18} className="text-[#C67D63]" />
            </div>
            <div>
              <h3 className="text-base font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                AI Insights
              </h3>
              <p className="text-xs text-[#5C635A] mt-0.5">
                Claude Sonnet 4.5 reads your selected surveys and writes a 3–5 paragraph executive summary covering sentiment, trends, themes, and recommended actions. Responses are already anonymous.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleAiSummary}
            disabled={aiLoading || selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#C67D63] hover:bg-[#a96550] text-white rounded-full text-sm font-medium transition-colors disabled:opacity-60 shadow-sm whitespace-nowrap"
            data-testid="reports-ai-generate"
          >
            {aiLoading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            {aiLoading ? 'Analysing…' : 'Analyse with AI'}
          </button>
        </div>

        {aiError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="reports-ai-error">
            {aiError}
          </div>
        )}

        {aiSummary && (
          <div
            className="mt-3 bg-white border border-[#E5E0D8] rounded-lg p-5"
            data-testid="reports-ai-output"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[#8E958C]">
                {aiSummary.surveys_count} survey{aiSummary.surveys_count === 1 ? '' : 's'} · {aiSummary.responses_count} responses · {aiSummary.model.replace('claude-', 'Claude ').replace('-20250929', '')}
              </p>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(aiSummary.summary);
                }}
                className="text-xs px-2 py-1 rounded-full border border-[#E5E0D8] text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="reports-ai-copy"
                title="Copy summary"
              >
                Copy
              </button>
            </div>
            <div
              className="prose-tmc text-[15px] text-[#2C302E] leading-relaxed whitespace-pre-wrap"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {aiSummary.summary}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating || selected.size === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full font-medium transition-colors disabled:opacity-60"
          data-testid="reports-generate"
        >
          {generating ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
          {generating ? 'Generating…' : 'Generate report'}
        </button>
      </div>
    </div>
  );
};

const FormatOption = ({ icon, label, hint, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left p-3 rounded-xl border transition-colors ${
      selected
        ? 'border-[#7A8B7A] bg-[#7A8B7A]/10'
        : 'border-[#E5E0D8] bg-white hover:bg-[#FDFBF7]'
    }`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-[#2C302E] font-medium text-sm">
      {icon}
      {label}
    </div>
    <p className="text-xs text-[#8E958C] mt-1">{hint}</p>
  </button>
);

export default ReportsTab;
